<template>
  <div class="flex w-full" style="position: relative">
    <div v-if="gliders.length" class="d-flex flex-col w-full h-full">
      <div class="flex-grow-0 w-full">
        <v-tabs v-if="showTabs" v-model="selectedGliderIdx" align-with-title class="flex-grow-0">
          <v-tab>
            <v-icon class="mr-2 !text-black">{{ fleetIco }}</v-icon>
            {{ fleet.FLE_LIBELLE }}
          </v-tab>
          <v-tab v-for="(glider, idx) in gliders" :key="glider.id" :title="glider.libelle">
            {{ glider.libelle }}
          </v-tab>
        </v-tabs>
      </div>
      <!-- broadcast flotte multi-gliders : pas de gliderFabricant passé aux glider-cmd-input (types hétérogènes → complétion complète) -->
      <div class="flex flex-col !p-2 gap-2" v-if="selectedGliderIdx == 0">
        <div class="bg-white !p-4">
          <span class="subheading">{{ $t('Pilot.NextSurfacing.NextSurfacing') }}</span>
          <div class="flex">
            <glider-cmd-input
              ref="nextSurfacingCmdInput"
              v-model="nextSurfacingCmd"
              @validSaisie="addBroadcastCmd('next')"
              :label="$t('Pilot.NextSurfacing.Command')"
            />
            <v-btn
              :loading="nextSavingCmd"
              :disabled="nextSavingCmd"
              small
              color="primary"
              @click.prevent="addBroadcastCmd('next')"
            >
              <v-icon small>mdi-plus</v-icon>
              {{ $t('Fleet.AddToAll') }}
            </v-btn>
          </div>
        </div>

        <div class="bg-white !p-4">
          <span class="subheading">{{ $t('Pilot.NextSurfacing.AllSurfacing') }}</span>
          <div class="flex">
            <glider-cmd-input
              ref="allSurfacingCmdInput"
              v-model="allSurfacingCmd"
              @validSaisie="addBroadcastCmd('all')"
              :label="$t('Pilot.NextSurfacing.Command')"
            />
            <v-btn
              :loading="allSavingCmd"
              :disabled="allSavingCmd"
              small
              color="primary"
              @click.prevent="addBroadcastCmd('all')"
            >
              <v-icon small>mdi-plus</v-icon>
              {{ $t('Fleet.AddToAll') }}
            </v-btn>
          </div>
        </div>

        <div class="bg-white !p-4">
          <span class="subheading">{{ $t('Pilot.NextSurfacing.NextConnect') }}</span>
          <div class="flex">
            <glider-cmd-input
              ref="nextConnectCmdInput"
              v-model="nextConnectCmd"
              @validSaisie="addBroadcastCmd('ncd')"
              :label="$t('Pilot.NextSurfacing.Command')"
            />
            <v-btn
              :loading="ncdSavingCmd"
              :disabled="ncdSavingCmd"
              small
              color="primary"
              @click.prevent="addBroadcastCmd('ncd')"
            >
              <v-icon small>mdi-plus</v-icon>
              {{ $t('Fleet.AddToAll') }}
            </v-btn>
          </div>
        </div>
      </div>
      <mission-detail-next-surfacing-do
        v-else
        :uiid="uiid"
        :isleft="isleft"
        :mission="store.myGetters.missionsById[gliders[selectedGliderIdx - 1].id]"
        :isVisible="props.isVisible"
        :key="selectedGliderIdx"
      />
    </div>
    <div v-else class="w-full h-full flex flex-col mx-4 items-center justify-center title gap-2">
      <span>{{ $t('Fleet.NoGliders') }}</span>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import store from '@/store'
import { AloDbFleet, isAloDbFleetCoverage, isAloDbFleetFormation } from '@/models/alodb/AloDbFleet'
import MissionDetailNextSurfacingDo from '@/components/mission/MissionDetailNextSurfacingDo.vue'
import GliderCmdInput from '@/components/shared/GliderCmdInput.vue'
import mission from '@/api/mission'

const props = defineProps<{
  fleet: AloDbFleet
  isleft: boolean
  uiid: string
  isVisible: boolean
}>()

const nextSurfacingCmd = ref('')
const allSurfacingCmd = ref('')
const nextConnectCmd = ref('')

const nextSavingCmd = ref(false)
const allSavingCmd = ref(false)
const ncdSavingCmd = ref(false)

const fleetIco = computed(() => {
  return isAloDbFleetCoverage(props.fleet)
    ? 'mdi-select-group'
    : isAloDbFleetFormation(props.fleet)
    ? 'mdi-vector-circle'
    : 'mdi-multicast'
})

const nextSurfacingCmdInput = ref<InstanceType<typeof GliderCmdInput> | null>(null)
const allSurfacingCmdInput = ref<InstanceType<typeof GliderCmdInput> | null>(null)
const nextConnectCmdInput = ref<InstanceType<typeof GliderCmdInput> | null>(null)

const missions = computed(
  () => props.fleet.activeMissionIds?.map(id => store.myGetters?.missionsById[id])?.filter(m => !!m) ?? [],
)

const gliders = computed(() => missions.value.map(m => ({ id: m.misId, libelle: m.gliKey })))

const selectedGliderIdx = ref(0)

const showTabs = ref(true)

function cleanCommande(cmd: String) {
  let corrected = cmd.replace(/ /g, '')
  if (!corrected.endsWith(';')) corrected = `${corrected};`
  return corrected
}

type BroadcastKind = 'next' | 'all' | 'ncd'

const broadcastBindings: Record<
  BroadcastKind,
  {
    cmdModel: typeof nextSurfacingCmd
    savingFlag: typeof nextSavingCmd
    inputRef: typeof nextSurfacingCmdInput
  }
> = {
  next: { cmdModel: nextSurfacingCmd, savingFlag: nextSavingCmd, inputRef: nextSurfacingCmdInput },
  all: { cmdModel: allSurfacingCmd, savingFlag: allSavingCmd, inputRef: allSurfacingCmdInput },
  ncd: { cmdModel: nextConnectCmd, savingFlag: ncdSavingCmd, inputRef: nextConnectCmdInput },
}

async function addBroadcastCmd(kind: BroadcastKind) {
  const { cmdModel, savingFlag, inputRef } = broadcastBindings[kind]
  if (savingFlag.value) return
  const cmd = cmdModel.value
  if (cmd.length === 0) return
  if (!(await inputRef.value?.checkSaisie())) return
  savingFlag.value = true
  const cmdCleaned = cleanCommande(cmd)

  for (const mission of missions.value.filter(m => m.misActifOn)) {
    if (mission.gliId == null) continue
    try {
      await store.myActions.GLIDER_IRIS_NSD_REQUEST(mission.gliId)
      const modSettings = store.myGetters.modifiedNsdConfigs[mission.gliId].mod_settings
      const currentList =
        kind === 'next'
          ? [...modSettings.NextSurfacingDo_next.listAction]
          : kind === 'all'
          ? [...modSettings.NextSurfacingDo_all.listAction]
          : [...(modSettings.NextConnectDo?.listAction ?? [])]
      currentList.push(cmdCleaned)
      const priority = [...modSettings.NextSurfacingDo.priority_listAction]
      const newPriority = kind === 'ncd' && !priority.includes('ncd') ? [...priority, 'ncd'] : priority
      const priorityUpdate = newPriority.length !== priority.length ? { priority_listAction: newPriority } : undefined
      await store.myActions.GLIDER_UPDATE_MODIFIED_NSD({
        itemId: mission.gliId,
        newProperties: {
          mod_settings: {
            ...(priorityUpdate ? { NextSurfacingDo: priorityUpdate } : {}),
            ...(kind === 'next' ? { NextSurfacingDo_next: { listAction: currentList } } : {}),
            ...(kind === 'all' ? { NextSurfacingDo_all: { listAction: currentList } } : {}),
            ...(kind === 'ncd' ? { NextConnectDo: { listAction: currentList } } : {}),
          },
        },
      })
      await store.myActions.GLIDER_IRIS_NSD_SAVE_REQUEST(mission.gliId)
    } catch (e) {
      //console.error("Error adding NSD command to mission", mission.misId, e)
    }
  }

  nextTick(() => {
    cmdModel.value = ''
    savingFlag.value = false
  })
}

onMounted(() => {
  setTimeout(() => {
    showTabs.value = false
    nextTick(() => {
      showTabs.value = true
    })
  }, 100)
})
</script>
